/**
 * Pure helpers extracted from useDashboardLayout.ts so jest can verify
 * the layout reconciliation contract without mounting React + driving
 * persistStore. The hook re-imports these — production behavior unchanged.
 */

import { WIDGET_REGISTRY, type DashboardLayout } from "./constants";

/**
 * Build a fresh default layout from the WIDGET_REGISTRY. Every widget
 * appears at its registry-declared position with its declared
 * defaultEnabled + defaultSize.
 */
export function getDefaultLayout(): DashboardLayout {
  return WIDGET_REGISTRY.map((w) => ({
    id: w.id,
    enabled: w.defaultEnabled,
    size: w.defaultSize,
    order: w.defaultOrder,
  }));
}

/**
 * Reconcile a previously-saved layout against the CURRENT registry.
 * Three responsibilities:
 *   1. Drop entries whose widget id no longer exists (removed widgets).
 *   2. Append any new registry widgets the saved layout doesn't know
 *      about (since the user last saved, we shipped new widgets — they
 *      should appear at the end with their registry defaults).
 *   3. Re-number `order` to a contiguous sequence starting at 0 so
 *      drag-drop has a stable index.
 *
 * Pure: takes the saved layout in, returns the merged layout out.
 * Doesn't read or write persistStore.
 */
export function mergeWithRegistry(saved: DashboardLayout): DashboardLayout {
  const savedMap = new Map(saved.map((item) => [item.id, item]));
  const registryIds = new Set(WIDGET_REGISTRY.map((w) => w.id));
  const merged: DashboardLayout = [];

  // Step 1 + reorder: keep saved entries that still exist in registry,
  // in their saved order
  for (const item of saved) {
    if (registryIds.has(item.id)) {
      merged.push(item);
    }
  }

  // Step 2: append any new widgets the saved layout didn't know about
  for (const widget of WIDGET_REGISTRY) {
    if (!savedMap.has(widget.id)) {
      merged.push({
        id: widget.id,
        enabled: widget.defaultEnabled,
        size: widget.defaultSize,
        order: merged.length,
      });
    }
  }

  // Step 3: re-number order to be contiguous starting at 0
  return merged.map((item, i) => ({ ...item, order: i }));
}
